import { computed, reactive, ref } from 'vue'
import { newCall } from '@/util/http'
import { ElMessage, ElMessageBox } from 'element-plus'
import { isEmpty, debounce } from '@safety/shared-utils'
import { CHART_KPIS, getKpiDefinition, KPI_MAP, METER_KPIS } from '../config/kpiRegistry'
import { CHART_SLOTS, createDefaultLayout, getChartSlot, iterateChartSlots, meterSlotKey, SLOT_KEYS } from '../config/slots'
import { applyMapModels, buildMapper, createMapRuntimeState, formatKpiWidgetData, formatUpdays, sortMapIds } from '../utils/dashboardFormat'
import { useChartResize, useFullscreen } from './useDashboardLayout'
import { useDashboardEvents } from './useDashboardEvents'

export function useDashboard() {
  const state = reactive({
    name: '',
    safeUpDays: ['0', '0', '0', '0'],
    riskLevelScores: null,
    layout: createDefaultLayout(),
    mapper: { hazardCategory: {}, area: {}, user: {}, tpl: {} },
    kpiData: {},
    mapOptions: [],
    mapRuntime: {},
    setting: false,
    mapOptionsLoaded: false
  })

  const slotRefs = reactive({})
  const riskMapRefs = ref([])
  const mainRef = ref(null)
  const kpiSelect = reactive({
    visible: false,
    multiple: false,
    slotKey: '',
    form: { kpiId: null, name: '' },
    values: []
  })

  const kpiOptions = computed(() => {
    if (kpiSelect.slotKey === 'meters') return METER_KPIS
    return CHART_KPIS
  })

  function formatWidgetProps(kpiId, rawData) {
    return formatKpiWidgetData(kpiId, rawData, {
      mapper: state.mapper,
      riskLevelScores: state.riskLevelScores,
      definition: getKpiDefinition(kpiId)
    })
  }

  const sortedMapIds = computed(() => sortMapIds(state.layout.mapIds, state.mapRuntime, state.mapper.area))

  function setSlotRef(slotKey, el) {
    slotRefs[slotKey] = el
  }

  function getResizeTargets() {
    const targets = CHART_SLOTS.map(({ key }) => slotRefs[key])
    targets.push(slotRefs[SLOT_KEYS.CENTER_FLOAT])
    return [...targets, ...riskMapRefs.value]
  }

  const { observe, disconnect, resizeAll } = useChartResize(getResizeTargets)
  const resize = debounce(resizeAll, 300)
  const { toggleFullScreen } = useFullscreen(resize)

  function findChartSlotByKpiId(kpiId) {
    let matched = null
    iterateChartSlots(state.layout, (slotKey, slot) => {
      if (slot.kpiId === kpiId) matched = slotKey
    })
    return matched
  }

  function updateLiveMessage(slotKey, data) {
    state.kpiData[slotKey] = data
  }

  function updateRiskLevel(levels) {
    const mapLevels = {}
    levels.forEach((item) => {
      mapLevels[item.mapId] = mapLevels[item.mapId] || []
      mapLevels[item.mapId].push(item)
    })
    Object.entries(mapLevels).forEach(([mapId, riskLevels]) => {
      if (state.mapRuntime[mapId]) state.mapRuntime[mapId].riskLevels = riskLevels
    })
  }

  function applyDashboardData(data) {
    state.name = data.name
    state.safeUpDays = formatUpdays(data.safeUpDays)
    state.riskLevelScores = data.riskLevelScores
    state.layout = data.layout
    state.mapper = buildMapper(data.meta)
    state.kpiData = data.kpiData || {}
    state.mapRuntime = createMapRuntimeState(data.layout.mapIds)
    applyMapModels(state.mapRuntime, data.mapModels || [], state.mapper.area)
  }

  async function loadDashboard() {
    const { code, data, message } = await newCall({ url: 'dashboard', loading: true })
    if (code !== 0) {
      ElMessage.error(message)
      throw new Error(message)
    }
    applyDashboardData(data)
    return data
  }

  async function refreshKpiItems(items) {
    const { code, data, message } = await newCall({
      url: 'dashboard/kpi',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: { items },
      loading: true
    })
    if (code !== 0) {
      ElMessage.error(message)
      return
    }
    Object.assign(state.kpiData, data)
    return data
  }

  function changeName() {
    ElMessageBox.prompt('请输入名称', '修改名称', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputValue: state.name
    })
      .then(async ({ value }) => {
        const { code, message } = await newCall({
          url: 'dashboard/name',
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: { name: value },
          loading: true
        })
        if (code !== 0) {
          ElMessage.error(message)
          return
        }
        state.name = value
        ElMessage.success('操作成功')
      })
      .catch(() => {})
  }

  function openKpiSelect(slotKey) {
    kpiSelect.slotKey = slotKey
    kpiSelect.multiple = slotKey === 'meters'
    if (kpiSelect.multiple) {
      kpiSelect.values = state.layout.meters.map((item) => ({
        kpiId: item.kpiId,
        name: item.name || KPI_MAP[item.kpiId]?.name || ''
      }))
    } else {
      const slot = getChartSlot(state.layout, slotKey)
      kpiSelect.form.kpiId = slot.kpiId
      kpiSelect.form.name = slot.name || KPI_MAP[slot.kpiId]?.name || ''
    }
    kpiSelect.visible = true
  }

  function handleKpiIdChange(kpiId, index) {
    const target = kpiSelect.multiple ? kpiSelect.values[index] : kpiSelect.form
    target.kpiId = kpiId
    target.name = KPI_MAP[kpiId]?.name || ''
  }

  async function confirmKpiSelect() {
    if (kpiSelect.multiple) {
      for (const item of kpiSelect.values) {
        if (isEmpty(item.kpiId)) return ElMessage.error('请选择内容类型')
        if (isEmpty(item.name)) return ElMessage.error('请输入显示名称')
      }
      state.layout.meters.splice(0, state.layout.meters.length, ...kpiSelect.values.map(({ kpiId, name }) => ({ kpiId, name, param: null })))
      kpiSelect.visible = false
      await refreshKpiItems(
        state.layout.meters.map((meter, index) => ({
          slotKey: meterSlotKey(index),
          kpiId: meter.kpiId,
          param: meter.param
        }))
      )
      return
    }
    if (isEmpty(kpiSelect.form.kpiId)) return ElMessage.error('请选择内容类型')
    if (isEmpty(kpiSelect.form.name)) return ElMessage.error('请输入显示名称')
    const slot = getChartSlot(state.layout, kpiSelect.slotKey)
    slot.kpiId = kpiSelect.form.kpiId
    slot.name = kpiSelect.form.name
    slot.param = null
    kpiSelect.visible = false
    await refreshKpiItems([{ slotKey: kpiSelect.slotKey, kpiId: slot.kpiId, param: slot.param }])
  }

  function toggleMapSelection(mapId) {
    const mapIds = state.layout.mapIds
    const index = mapIds.indexOf(mapId)
    if (index >= 0) mapIds.splice(index, 1)
    else mapIds.push(mapId)
  }

  async function enterSetting() {
    if (!state.mapOptionsLoaded) {
      const { code, data, message } = await newCall({ url: 'dashboard/map-options', loading: true })
      if (code !== 0) {
        ElMessage.error(message)
        return
      }
      state.mapOptions = data
      state.mapOptionsLoaded = true
    }
    state.setting = true
  }

  async function exitSetting() {
    const { code, data, message } = await newCall({
      url: 'dashboard/config',
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: { layout: state.layout },
      loading: true
    })
    if (code !== 0) {
      ElMessage.error(message)
      return
    }
    state.mapRuntime = createMapRuntimeState(state.layout.mapIds)
    applyMapModels(state.mapRuntime, data.mapModels || [], state.mapper.area)
    state.setting = false
  }

  async function toggleSetting() {
    if (!state.setting) await enterSetting()
    else await exitSetting()
  }

  useDashboardEvents({ updateRiskLevel, updateLiveMessage, findChartSlotByKpiId })

  function setRiskMapRef(index, el) {
    riskMapRefs.value[index] = el
  }

  return {
    state,
    slotRefs,
    riskMapRefs,
    mainRef,
    kpiSelect,
    kpiOptions,
    sortedMapIds,
    setSlotRef,
    formatWidgetProps,
    loadDashboard,
    changeName,
    openKpiSelect,
    handleKpiIdChange,
    confirmKpiSelect,
    toggleMapSelection,
    toggleSetting,
    toggleFullScreen,
    observe,
    disconnect,
    resize,
    setRiskMapRef
  }
}
